/**
 * Double progression — architecture §6 step 3/4 (the deterministic option set the
 * LLM is allowed to choose from).
 *
 * For every `plan_exercises` row:
 *   all prescribed sets hit `repMax` at RPE <= targetRpe + 0.5  -> +increment
 *   sets landed inside repMin..repMax but not at repMax         -> hold load, repMax + 1
 *   a set missed repMin, or a set failed                        -> -5% (nearest 0.5 kg)
 *   verdict `deload_recommended`                                -> sets x0.6 (min 2), load -10%
 *   verdict `insufficient_data`                                 -> no changes, needsMoreData
 *
 * The packet normally carries week-level aggregates, so "did every set hit repMax?"
 * is answered from evidence that *is* in the packet:
 *   1. `exerciseTrend[].sets` when a caller attaches exact set rows (preferred), or
 *   2. the inferred reps at the top weight, `30 * (bestE1rm / topWeight - 1)`,
 *      which is exact for the Epley model the database uses.
 * Both paths are covered by the tests; `inferTopSetReps` is exported for audit.
 */
import type { CoachInput, ExerciseTrendPoint } from '../types/coach.js';
import type {
  EvaluationResult,
  PlanProposal,
  PrescribedChange,
  PrescribedChangeField,
} from '../types/results.js';
import { flattenPlanExercises } from './matrix.js';
import {
  LOAD_INCREMENT_ACCESSORY_KG,
  LOAD_INCREMENT_COMPOUND_KG,
  MIN_LOAD_STEP_KG,
  RPE_TOLERANCE,
  THRESHOLDS,
} from './matrix.js';
import { round2, roundToIncrement } from './math.js';

/** Sets are never cut below this, even in a deload week. */
export const MIN_DELOAD_SETS = 2;
/** Deload volume multiplier. */
export const DELOAD_VOLUME_FACTOR = 0.6;
/** Deload intensity reduction. */
export const DELOAD_INTENSITY_FACTOR = 0.9;
/** Bodyweight movements earn a rep before they earn added weight. */
export const BODYWEIGHT_ADDED_WEIGHT_KG = 2.5;
/** Load reduction applied when a set misses the bottom of the rep range. */
export const MISSED_REP_REDUCTION_FACTOR = 0.05;

const COMPOUND_KEYWORDS = [
  'squat',
  'bench',
  'deadlift',
  'press',
  'row',
  'pull-up',
  'pull up',
  'chin-up',
  'chin up',
  'dip',
  'lunge',
  'clean',
  'snatch',
  'thruster',
  'push-up',
  'push up',
];

const ACCESSORY_KEYWORDS = [
  'curl',
  'pushdown',
  'raise',
  'extension',
  'fly',
  'flye',
  'pulldown',
  'push-down',
  'kickback',
  'shrug',
  'calf',
  'face pull',
  'pull-through',
  'ab ',
  'crunch',
  'plank',
];

/** True for movements progressed on reps rather than on an e1RM. */
export function isBodyweightMovement(exerciseName: string, topWeights: ReadonlyArray<number>): boolean {
  const lowered = exerciseName.toLowerCase();
  const keyword =
    lowered.includes('pull-up') ||
    lowered.includes('pull up') ||
    lowered.includes('chin-up') ||
    lowered.includes('chin up') ||
    lowered.includes('push-up') ||
    lowered.includes('push up') ||
    lowered.includes('dip') ||
    lowered.includes('muscle-up') ||
    lowered.includes('inverted row') ||
    lowered.includes('bodyweight');
  const neverLoaded = topWeights.every((weight) => !Number.isFinite(weight) || weight <= 0);
  return keyword && neverLoaded;
}

/** True for barbell/compound lifts, which take a 2.5 kg jump. */
export function isCompoundMovement(exerciseName: string): boolean {
  const lowered = exerciseName.toLowerCase();
  if (ACCESSORY_KEYWORDS.some((keyword) => lowered.includes(keyword))) {
    // "close-grip bench" and "barbell curl" contain compound words but are accessory work.
    if (lowered.includes('close-grip') || lowered.includes('close grip')) return false;
    if (lowered.includes('bench') || lowered.includes('press') || lowered.includes('squat')) {
      return true;
    }
    return false;
  }
  return COMPOUND_KEYWORDS.some((keyword) => lowered.includes(keyword));
}

/** Load step for a movement: 2.5 kg compound, 1.25 kg accessory, 2.5 kg added weight. */
export function loadIncrementKg(exerciseName: string, bodyweight: boolean): number {
  if (bodyweight) return BODYWEIGHT_ADDED_WEIGHT_KG;
  return isCompoundMovement(exerciseName)
    ? LOAD_INCREMENT_COMPOUND_KG
    : LOAD_INCREMENT_ACCESSORY_KG;
}

/**
 * Reps implied by the best Epley e1RM at the top weight. Exact for the SQL model
 * (`e1rm = w * (1 + min(reps,12)/30)`), rounded to the nearest whole rep.
 */
export function inferTopSetReps(point: ExerciseTrendPoint): number | null {
  const top = point.topWeightKg ?? 0;
  const e1rm = point.bestE1rmKg ?? 0;
  if (top <= 0 || e1rm <= 0) return null;
  const ratio = e1rm / top;
  if (!Number.isFinite(ratio) || ratio <= 1) return null;
  return Math.round(30 * (ratio - 1));
}

interface SetEvidence {
  /** Reps of the final working set, when it can be determined. */
  lastSetReps: number | null;
  /** True when any working set in the week was logged as failed. */
  hasFailedSet: boolean;
  /** Reps across the whole week, when exact set rows are available. */
  allReps: number[] | null;
}

function weekSetEvidence(point: ExerciseTrendPoint): SetEvidence {
  const sets = point.sets;
  if (sets && sets.length > 0) {
    const working = sets.filter((set) => set.status !== 'warmup');
    const scored = working.length > 0 ? working : sets;
    const last = scored[scored.length - 1];
    return {
      lastSetReps: last ? last.reps : null,
      hasFailedSet: scored.some((set) => set.status === 'failed') || point.failedSets > 0,
      allReps: scored.map((set) => set.reps),
    };
  }
  return {
    lastSetReps: inferTopSetReps(point),
    hasFailedSet: point.failedSets > 0,
    allReps: null,
  };
}

function change(
  base: Pick<PrescribedChange, 'planExerciseId' | 'exerciseId' | 'exerciseName' | 'dayId' | 'dayLabel'>,
  action: PrescribedChange['action'],
  rule: string,
  rationale: string,
  fields: PrescribedChangeField[],
  note?: string,
): PrescribedChange {
  const result: PrescribedChange = {
    planExerciseId: base.planExerciseId,
    exerciseId: base.exerciseId,
    exerciseName: base.exerciseName,
    dayId: base.dayId,
    dayLabel: base.dayLabel,
    action,
    rule,
    rationale,
    fields,
  };
  if (note !== undefined) result.note = note;
  return result;
}

function field(
  name: PrescribedChangeField['field'],
  before: number | null,
  after: number | null,
  unit: string,
): PrescribedChangeField {
  return { field: name, before, after, unit };
}

/** `-5%`, snapped to the nearest 0.5 kg plate pair. */
export function reduceLoadFivePercent(weightKg: number): number {
  const reduced = weightKg * (1 - MISSED_REP_REDUCTION_FACTOR);
  return Math.max(roundToIncrement(reduced, MIN_LOAD_STEP_KG), MIN_LOAD_STEP_KG);
}

function weekFor(packet: CoachInput): number {
  const plan = packet.activePlan;
  if (!plan) return 1;
  // `workout_plans.weeks` holds the programme length; the coach always writes the next week.
  return Math.max(1, plan.week + 1);
}

/**
 * Build next week's prescription. Pure and deterministic — it reads only the packet
 * and the evaluation, never the clock.
 */
export function proposeNextWeek(packet: CoachInput, evaluation: EvaluationResult): PlanProposal {
  const plan = packet.activePlan;
  const nextWeek = weekFor(packet);

  if (!plan) {
    return {
      planId: null,
      planName: null,
      week: nextWeek,
      changes: [],
      notes: ['No active plan. Upload or pick a program and the coach will write changes into it.'],
      needsMoreData: false,
      basedOnVerdict: evaluation.verdict,
    };
  }

  if (evaluation.verdict === 'insufficient_data') {
    return {
      planId: plan.planId,
      planName: plan.name,
      week: nextWeek,
      changes: [],
      notes: [
        'Not enough data to change the plan yet — keep the current prescription for one more week.',
        `Two sessions a week for two weeks is enough to call it; this window has ${evaluation.metrics.sessionsAnalyzed} session(s) across ${evaluation.metrics.weeksAnalyzed} week(s).`,
      ],
      needsMoreData: true,
      basedOnVerdict: evaluation.verdict,
    };
  }

  const insightByExercise = new Map(
    evaluation.exerciseInsights.map((insight) => [insight.exerciseId, insight]),
  );
  // Latest week that has trend data for each exercise.
  const latestPointByExercise = new Map<string, ExerciseTrendPoint>();
  for (const point of packet.exerciseTrend) {
    const existing = latestPointByExercise.get(point.exerciseId);
    if (!existing || point.weekStart > existing.weekStart) {
      latestPointByExercise.set(point.exerciseId, point);
    }
  }

  const changes: PrescribedChange[] = [];
  const notes: string[] = [];

  for (const { dayId, dayLabel, exercise } of flattenPlanExercises(plan)) {
    const base = {
      planExerciseId: exercise.planExerciseId,
      exerciseId: exercise.exerciseId,
      exerciseName: exercise.name,
      dayId,
      dayLabel,
    };

    const point = latestPointByExercise.get(exercise.exerciseId);
    const insight = insightByExercise.get(exercise.exerciseId);
    const insightNote = insight
      ? `Matrix call: ${insight.recommendation} — ${insight.reason}`
      : 'No per-exercise matrix insight for this movement; the logged sets drove this change.';

    if (!point) {
      changes.push(
        change(
          base,
          'no_change',
          'double_progression.no_evidence',
          `No logged working sets for ${exercise.name} in this window. Keep it as prescribed and log it next session.`,
          [],
          insightNote,
        ),
      );
      continue;
    }

    const bodyweight = isBodyweightMovement(
      exercise.name,
      packet.exerciseTrend
        .filter((trendPoint) => trendPoint.exerciseId === exercise.exerciseId)
        .map((trendPoint) => trendPoint.topWeightKg ?? 0),
    );

    // --- deload wins over everything -------------------------------------
    if (evaluation.verdict === 'deload_recommended') {
      const newSets = Math.max(Math.floor(exercise.sets * DELOAD_VOLUME_FACTOR), MIN_DELOAD_SETS);
      // The deload is computed from the prescription — that is the number the plan
      // will actually be written with, and the number the report shows as "before".
      const currentWeight = exercise.targetWeightKg ?? 0;
      const newWeight =
        currentWeight > 0
          ? Math.max(roundToIncrement(currentWeight * DELOAD_INTENSITY_FACTOR, MIN_LOAD_STEP_KG), MIN_LOAD_STEP_KG)
          : exercise.targetWeightKg;
      const currentRpe = exercise.targetRpe ?? 8;
      const newRpe = Math.max(5, Math.min(8, round2(currentRpe - 1)));
      changes.push(
        change(
          base,
          'deload',
          'deload.volume_60_intensity_90',
          `${exercise.name} earned the work — RPE says you have not recovered from it. Next week: ${newSets} sets at ${newWeight !== null ? `${newWeight} kg` : 'bodyweight'} instead of ${exercise.sets} at ${currentWeight > 0 ? `${currentWeight} kg` : 'bodyweight'}, and stop at RPE ${newRpe}.`,
          [
            field('sets', exercise.sets, newSets, 'sets'),
            field('repMin', exercise.repMin, exercise.repMin, 'reps'),
            field('repMax', exercise.repMax, exercise.repMax, 'reps'),
            field('targetWeightKg', exercise.targetWeightKg ?? null, newWeight ?? null, 'kg'),
            field('targetRpe', exercise.targetRpe ?? null, newRpe, 'rpe'),
          ],
          `Deload week: volume and load come down so the next block starts fresh. Deload beats any increase. ${insightNote}`,
        ),
      );
      continue;
    }

    const evidence = weekSetEvidence(point);
    const repsAtTop = inferTopSetReps(point);
    const targetRpe = exercise.targetRpe ?? 8;
    const latestRpe = point.avgRpe ?? null;
    const topWeight = point.topWeightKg ?? 0;

    if (latestRpe !== null && latestRpe > targetRpe + RPE_TOLERANCE) {
      changes.push(
        change(
          base,
          'no_change',
          'double_progression.rpe_above_target',
          `${exercise.name} came in at RPE ${latestRpe.toFixed(2)} against a target of ${targetRpe.toFixed(1)}. Hold the load — no load increase until it sits inside the target.`,
          [field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe')],
          insightNote,
        ),
      );
      continue;
    }

    // --- missed the bottom of the range: take 5% off ----------------------
    const missedReps =
      (evidence.lastSetReps !== null && evidence.lastSetReps < exercise.repMin) || evidence.hasFailedSet;
    if (missedReps) {
      const beforeWeight = topWeight > 0 ? topWeight : (exercise.targetWeightKg ?? 0);
      const afterWeight = beforeWeight > 0 ? reduceLoadFivePercent(beforeWeight) : beforeWeight;
      const missedText =
        evidence.lastSetReps !== null && evidence.lastSetReps < exercise.repMin
          ? `the last set stopped at ${evidence.lastSetReps} reps against a ${exercise.repMin}-rep floor`
          : 'a set was logged as failed';
      changes.push(
        change(
          base,
          'reduce_load',
          evidence.lastSetReps !== null && evidence.lastSetReps < exercise.repMin
            ? 'double_progression.last_set_below_rep_min'
            : 'double_progression.failed_set',
          `${exercise.name}: ${missedText}. Take 5% off to ${afterWeight} kg and own ${exercise.repMin} reps before you add anything back.`,
          [
            field('sets', exercise.sets, exercise.sets, 'sets'),
            field('repMin', exercise.repMin, exercise.repMin, 'reps'),
            field('repMax', exercise.repMax, exercise.repMax, 'reps'),
            field('targetWeightKg', exercise.targetWeightKg ?? beforeWeight, afterWeight, 'kg'),
            field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe'),
          ],
          insightNote,
        ),
      );
      continue;
    }

    const hitTop = evidence.allReps
      ? evidence.allReps.every((reps) => reps >= exercise.repMax)
      : repsAtTop !== null && repsAtTop >= exercise.repMax;

    // --- every set hit the top of the range: increase ---------------------
    if (hitTop) {
      if (bodyweight) {
        const atRepCeiling = exercise.repMax >= exercise.repMin + 4;
        if (atRepCeiling) {
          const beforeWeight = exercise.targetWeightKg ?? 0;
          changes.push(
            change(
              base,
              'increase_load',
              'double_progression.bodyweight_added_weight',
              `${exercise.name} is at the top of the range at bodyweight. Add ${BODYWEIGHT_ADDED_WEIGHT_KG} kg and rebuild from ${exercise.repMin} reps.`,
              [
                field('sets', exercise.sets, exercise.sets, 'sets'),
                field('repMin', exercise.repMin, exercise.repMin, 'reps'),
                field('repMax', exercise.repMax, exercise.repMax, 'reps'),
                field('targetWeightKg', beforeWeight, round2(beforeWeight + BODYWEIGHT_ADDED_WEIGHT_KG), 'kg'),
                field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe'),
              ],
            ),
          );
        } else {
          changes.push(
            change(
              base,
              'hold_and_add_rep',
              'double_progression.bodyweight_add_rep',
              `${exercise.name} hit the top of the range at bodyweight. Earn one more rep: target ${exercise.repMax + 1} next session, load unchanged.`,
              [
                field('sets', exercise.sets, exercise.sets, 'sets'),
                field('repMin', exercise.repMin, exercise.repMin, 'reps'),
                field('repMax', exercise.repMax, exercise.repMax + 1, 'reps'),
                field('targetWeightKg', exercise.targetWeightKg ?? null, exercise.targetWeightKg ?? null, 'kg'),
                field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe'),
              ],
            ),
          );
        }
        continue;
      }

      const increment = loadIncrementKg(exercise.name, bodyweight);
      const beforeWeight = topWeight > 0 ? topWeight : (exercise.targetWeightKg ?? 0);
      const afterWeight = round2(beforeWeight + increment);
      changes.push(
        change(
          base,
          'increase_load',
          'double_progression.top_of_range',
          `${exercise.name} hit ${exercise.repMax} reps at RPE ${latestRpe !== null ? latestRpe.toFixed(2) : 'target'}. That is earned overload: ${afterWeight} kg next week, same ${exercise.sets} sets.`,
          [
            field('sets', exercise.sets, exercise.sets, 'sets'),
            field('repMin', exercise.repMin, exercise.repMin, 'reps'),
            field('repMax', exercise.repMax, exercise.repMax, 'reps'),
            field('targetWeightKg', exercise.targetWeightKg ?? beforeWeight, afterWeight, 'kg'),
            field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe'),
          ],
        ),
      );
      continue;
    }

    // --- inside the range: hold the load, take the top rep further ---------
    const inferredText = repsAtTop !== null ? `${repsAtTop} reps at ${topWeight} kg` : 'inside the rep range';
    changes.push(
      change(
        base,
        'hold_and_add_rep',
        'double_progression.hold_and_add_rep',
        `${exercise.name} landed at ${inferredText}. Hold the load and add one rep to the target — top of the range first, then load.`,
        [
          field('sets', exercise.sets, exercise.sets, 'sets'),
          field('repMin', exercise.repMin, exercise.repMin, 'reps'),
          field('repMax', exercise.repMax, exercise.repMax + 1, 'reps'),
          field('targetWeightKg', exercise.targetWeightKg ?? null, exercise.targetWeightKg ?? null, 'kg'),
          field('targetRpe', exercise.targetRpe ?? null, exercise.targetRpe ?? null, 'rpe'),
        ],
      ),
    );
  }

  if (insightByExercise.size === 0) {
    notes.push('No per-exercise trend data in the window; changes fall back to the logged sets in the plan.');
  }
  if (evaluation.metrics.compliancePct < THRESHOLDS.COMPLIANCE_COLLAPSE_PCT) {
    notes.push(
      `Compliance is ${evaluation.metrics.compliancePct.toFixed(1)}%. Load changes assume the sessions get logged — the plan itself is fine.`,
    );
  }

  return {
    planId: plan.planId,
    planName: plan.name,
    week: nextWeek,
    changes,
    notes,
    needsMoreData: false,
    basedOnVerdict: evaluation.verdict,
  };
}
